import { useParams, useSearch } from '@tanstack/react-router'
import { useLingui } from '@lingui/react/macro'
import { deriveProfiles } from '@/hooks/use-root-search'
import { Skeleton } from '@/components/ui/skeleton'
import { SectionLabel } from '@/components/custom/section-label'
import { FetchingBarSkeleton, ListSkeleton } from './shared/list-skeleton'
import { FlowFrame } from './shared/flow-frame'
import { AuthLayout } from './auth/auth-layout'
import { SCROLLABLE_AREA_CLASS } from './shared/scrollable-area-class'
import {
  CONTROL_GROUP_BOX,
  CONTROL_HEAD_TYPE,
  GROUPED_HEAD_BOX,
  STRIP_CHROME,
  STRIP_EDGE,
  STRIP_GROUP_HEIGHT,
  STRIP_PAD_Y,
  STRIP_ROW,
} from './shared/strip-metrics'
import { SCORE_BADGE_BOX, SCORE_CLUSTER_CLASS, SCORE_COLUMN_CLASS, SCORE_GLOBAL_CLASS } from './shared/score-metrics'
import { cn } from '@/lib/utils'
import { useRootChrome } from '@/components/custom/route-transition'

/**
 * Which chrome a route draws. `full` and the signed-in `simple` (the test steps) and `bare` (checkout, the tools) are
 * `_app`'s; `_public`'s pages are `bare` except the register screens, which are `simple` for a visitor already
 * signed in mid-registration (the user menu, and nothing else).
 */
export function getChromeVariant(routeId: string): 'full' | 'simple' | 'bare' | 'none' {
  if (routeId.startsWith('/_public')) return routeId.startsWith('/_public/register') ? 'simple' : 'bare'
  if (!routeId.startsWith('/_app')) return 'none'
  if (routeId.startsWith('/_app/test')) return 'simple'
  if (routeId.startsWith('/_app/checkout') || routeId.startsWith('/_app/beta')) return 'bare'
  return 'full'
}

export function LogoSkeleton() {
  return <Skeleton className="h-6 w-40 shrink-0" />
}

export function NavSkeleton() {
  return (
    <nav className="hidden flex-1 items-center gap-1 md:flex">
      <Skeleton className="h-9 w-20 rounded-lg" />
      <Skeleton className="h-9 w-20 rounded-lg" />
      <Skeleton className="h-9 w-20 rounded-lg" />
    </nav>
  )
}

// The mobile bottom bar's placeholder (app-bottom-nav.tsx). Mirrors the real bar's box metrics — p-1
// outer, per-cell py-1.5 + size-5 icon + gap-0.5 + a 10px/leading-tight label — so <main> doesn't resize
// when the roster resolves.
export function BottomNavSkeleton() {
  return (
    <nav className="flex items-stretch gap-0.5 p-1">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex min-w-0 flex-1 flex-col items-center gap-0.5 px-1 py-1.5">
          <Skeleton className="size-5 shrink-0 rounded" />
          <Skeleton className="h-3 w-10 rounded" />
        </div>
      ))}
    </nav>
  )
}

export function UserMenuSkeleton() {
  return <Skeleton className="ml-auto size-9 shrink-0 rounded-full md:ml-0" />
}

/**
 * `stacked` marks the five sections that become a nav stack on a phone (employees, jobs, teams, offers, orgs).
 * There TitleSection hides below `md` and each level carries `.ns-head` instead — 48px with a HARD rule, against
 * the desktop title row's 56px and fading one — so the skeleton has to swap headers at `md` the same way.
 */
export function SectionHeaderSkeleton(props: { breadcrumb?: boolean; stacked?: boolean }) {
  // TitleSection's own rule: outside a stack the phone's in-pane bar sits above the title row, so the skeleton's
  // row lands under it too rather than at the top of the pane, under the pinned bar.
  const rootChrome = useRootChrome()
  return (
    <>
      {!props.stacked && rootChrome}
      {props.stacked && (
        <div className="flex h-12 shrink-0 items-center gap-2 border-b px-3 md:hidden">
          <Skeleton className="h-4 w-32" />
        </div>
      )}
      <SectionTitleRowSkeleton breadcrumb={props.breadcrumb} stacked={props.stacked} />
    </>
  )
}

function SectionTitleRowSkeleton(props: { breadcrumb?: boolean; stacked?: boolean }) {
  return (
    <div
      className={cn(
        'rule-fade-ends relative flex shrink-0 items-center justify-between gap-2 p-3 sm:gap-4 sm:p-5',
        props.stacked && 'max-md:hidden',
      )}
    >
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        <Skeleton className="size-8 shrink-0 rounded-lg sm:size-9" />
        <div className="flex h-6 items-center sm:h-7">
          <Skeleton className="h-4 w-28 sm:w-40" />
        </div>
        {props.breadcrumb && (
          <>
            <Skeleton className="size-3 shrink-0 rounded-sm" />
            <div className="flex h-6 items-center sm:h-7">
              <Skeleton className="h-4 w-24 sm:w-32" />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * The list strip (shared/score-controls.tsx) as a skeleton: its own box classes, so the pending row is the
 * same 45px the real one is. `edge` picks the same rule the real strip picks for that pane. `spelledOut`: the
 * strip is ONLY its three heads, which then print their full names (Empleados, which has nothing to rank by).
 * `heads`: a report is open beside it; without one Empleados shows only the count.
 */
function StripSkeleton(props: { edge?: 'panel' | 'left'; spelledOut?: boolean; heads?: boolean }) {
  return (
    <div className={cn(STRIP_ROW, STRIP_PAD_Y, STRIP_CHROME, STRIP_EDGE[props.edge ?? 'panel'])}>
      {/* The count the strip carries, in the label box the real one renders into (score-controls.tsx). */}
      <SectionLabel className="block min-w-0 flex-1 truncate">
        <Skeleton className="inline-block h-2.5 w-20 align-middle" />
      </SectionLabel>
      {props.spelledOut ? (
        props.heads ? (
          <SpelledOutHeadsSkeleton />
        ) : (
          <span className={STRIP_GROUP_HEIGHT} />
        )
      ) : (
        <>
          <Skeleton className="h-7 w-30 shrink-0 rounded-md" />
          <Skeleton className="h-7 w-11 shrink-0 rounded-md" />
          <Skeleton className="h-7 w-7 shrink-0 rounded-md" />
        </>
      )}
    </div>
  )
}

/** A default-size `Button` with a leading icon, sized by its real label set invisibly in the same type. */
function LabelledActionSkeleton(props: { label: string }) {
  return (
    <Skeleton className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-transparent px-2.5 text-sm font-medium whitespace-nowrap">
      <span className="size-4 shrink-0" />
      <span className="invisible">{props.label}</span>
    </Skeleton>
  )
}

/** The heads' group, sized by the real translated names set invisibly in the real head boxes: no locale drifts. */
function SpelledOutHeadsSkeleton() {
  const { t } = useLingui()
  return (
    <Skeleton className={cn(CONTROL_GROUP_BOX, 'border-transparent')}>
      {[t`Competences`, t`Values`, t`Personality`].map((label) => (
        <span key={label} className={cn(CONTROL_HEAD_TYPE, GROUPED_HEAD_BOX, 'invisible')}>
          {label}
        </span>
      ))}
    </Skeleton>
  )
}

/** The selection hint that heads every list (collapsible.tsx), at the height it settles to once it has grown in. */
function SelectionHintSkeleton() {
  return (
    <div className="flex items-center gap-1.5 px-1 py-1">
      <Skeleton className="size-3.5 shrink-0 rounded-full" />
      {/* The hint's own `text-xs` line box, which is taller than the bar standing in for the words. */}
      <span className="text-xs">
        <Skeleton className="inline-block h-3 w-52 align-middle" />
      </span>
    </div>
  )
}

/**
 * The body a squashed list pane draws: CollapsibleToolbar's two stacked actions over the rail list
 * (collapsible.tsx). Its own box only — the pane around it belongs to whoever squashed it.
 */
export function ListRailBodySkeleton() {
  return (
    <>
      <div className="rule-fade-left relative flex shrink-0 flex-col items-start gap-3.5 px-2 py-3">
        <Skeleton className="size-8 shrink-0 rounded-lg" />
        <Skeleton className="size-8 shrink-0 rounded-lg" />
      </div>
      {/* No selection hint: at a detail url the real one is collapsed, and its `-mb-1` cancels the gap it costs. */}
      <div className="flex min-h-0 flex-col gap-1 overflow-hidden px-2 py-3">
        <FetchingBarSkeleton />
        <ListSkeleton collapsed />
      </div>
    </>
  )
}

/**
 * `strip`: the pane heads its list with the list strip, which carries the count — Empleados. The section RAILS
 * (the offers/positions/teams lists) have no strip and lead their rows with a count label of their own instead.
 * `actions`: how many icon buttons follow the search box; `actionLabel` replaces them with ONE labelled button.
 * `hint`: whether the selection hint shows. `collapsed`: the pane has squashed to the rail, which would
 * otherwise clip the full list body to 52px.
 */
export function ListPanelBodySkeleton(props: {
  strip?: boolean
  actions?: number
  actionLabel?: string
  hint?: boolean
  collapsed?: boolean
}) {
  if (props.collapsed) return <ListRailBodySkeleton />
  const hint = props.hint ?? true
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* The search row: `px-4 py-3` and the same faded rule the real toolbars close themselves with — a
          `border-b` would render outside the padding box and make the row a pixel taller than all five. */}
      <div className="rule-fade-left relative flex shrink-0 items-center gap-2 px-4 py-3">
        <Skeleton className="h-8 min-w-0 flex-1 rounded-lg" />
        {props.actionLabel ? (
          <LabelledActionSkeleton label={props.actionLabel} />
        ) : (
          // The toolbar's actions are icon-only (CollapsibleToolbar.Action), `size="icon"` buttons.
          Array.from({ length: props.actions ?? 1 }, (_, i) => (
            <Skeleton key={i} className="size-8 shrink-0 rounded-lg" />
          ))
        )}
      </div>
      {/* One box sized exactly like the real list (ScrollableList). With a strip it is `fitContent`, whose
          missing top padding is what lets that strip's rule sit where the real one does. */}
      <div
        className={cn(
          'flex min-h-0 flex-col gap-1 overflow-hidden px-3 pb-3 max-md:shrink-0 max-md:grow md:flex-1',
          !props.strip && 'pt-3',
        )}
      >
        {/* With a strip the real list is `fitContent`, where the bar reserves nothing (`h-0 -mb-1`). */}
        {props.strip ? <StripSkeleton edge="left" spelledOut heads={!hint} /> : <FetchingBarSkeleton />}
        {hint && <SelectionHintSkeleton />}
        <ListSkeleton showLabel={!props.strip} tallAvatar={props.strip} />
      </div>
    </div>
  )
}

function AuthFieldSkeleton() {
  return (
    <div className="grid w-full gap-2">
      <div className="flex h-5 items-center">
        <Skeleton className="h-3.5 w-24" />
      </div>
      <Skeleton className="h-8 w-full rounded-lg" />
    </div>
  )
}

export function AuthCardSkeleton(props: {
  logo?: boolean
  social?: boolean
  terms?: boolean
  forgot?: boolean
  fields?: number
}) {
  const fields = props.fields ?? 2
  const card = (
    <div className="mx-auto flex w-full max-w-120 flex-col items-center gap-6 p-6">
      {props.logo && <Skeleton className="h-7 w-32" />}
      <div className="flow-header flex flex-col items-center gap-2 text-center">
        {/* Tracks the title's own `text-3xl sm:text-4xl` line box, so the heading does not jump when it paints. */}
        <Skeleton className="h-9 w-48 sm:h-11" />
        <Skeleton className="h-4 w-64" />
      </div>
      <FlowFrame contentClassName="flex flex-col gap-5">
        {props.social && (
          <>
            <div className="flex flex-col gap-2">
              <Skeleton className="h-10 w-full rounded-lg" />
              <Skeleton className="h-10 w-full rounded-lg" />
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 border-t" />
              <Skeleton className="h-3 w-4" />
              <div className="flex-1 border-t" />
            </div>
          </>
        )}
        <div className="flex flex-col gap-3">
          {Array.from({ length: fields }, (_, i) => (
            <AuthFieldSkeleton key={i} />
          ))}
          {props.terms && (
            <div className="my-3 flex gap-2">
              <Skeleton className="size-4 shrink-0 rounded" />
              <div className="flex flex-1 flex-col gap-1.5">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-2/3" />
              </div>
            </div>
          )}
          {props.forgot && <Skeleton className="h-4 w-40" />}
          <Skeleton className="h-8 w-full rounded-lg" />
        </div>
      </FlowFrame>
      <Skeleton className="h-4 w-44" />
    </div>
  )

  // `logo` marks the ENTRY screens — every way in, including the emailed ones — the ones AuthCard centres and brands.
  return props.logo ? <AuthLayout>{card}</AuthLayout> : card
}

export function AuthPending() {
  return <AuthCardSkeleton logo fields={1} />
}

/**
 * ComparisonProfiles' own header (comparison-profiles.tsx), which is NOT a CollapsibleSection: it ramps its
 * padding and icon with the pane instead of sitting at a fixed py-8, and ends in ReportViewControls' two
 * bordered pills rather than a collapse toggle.
 */
function ProfileHeaderSkeleton() {
  return (
    <div className="flex w-full gap-3 py-6 @sm:items-center @sm:gap-4 @sm:py-8">
      <div className="flex min-w-0 flex-1 flex-col gap-2 @sm:gap-3">
        <div className="flex items-center gap-2 @sm:gap-3">
          <Skeleton className="size-8 shrink-0 rounded-xl @sm:size-10" />
          <div className="flex h-7 items-center">
            <Skeleton className="h-4 w-36 @sm:w-44" />
          </div>
        </div>
        <div className="flex h-[15px] items-center @sm:h-5">
          <Skeleton className="h-3 w-56 @sm:h-3.5" />
        </div>
      </div>
      {/* The real pills paint their own border at once and only their controls arrive, so the skeleton keeps
          the border and fills the controls (report-view-controls.tsx). */}
      <div className="flex shrink-0 items-center gap-3">
        <div className="flex items-center gap-1 rounded-lg border p-0.5">
          <Skeleton className="size-7 rounded-md" />
          <Skeleton className="size-7 rounded-md" />
        </div>
        <div className="flex items-center rounded-full border p-0.5">
          <Skeleton className="size-7 rounded-full" />
        </div>
      </div>
    </div>
  )
}

/**
 * ProfileCard (profile-card.tsx): the avatar, its badge-over-name column with the Puesto line under it, the
 * pair of round actions, and the meta row over the Select group. Every text row carries the real card's type
 * classes and `min-h-[1lh]`, so its box IS that line box rather than a utility height rounded to the pixel.
 */
function ProfileCardSkeleton(props: { compare?: boolean; panelOwnsSelect?: boolean; source?: boolean }) {
  // The Select group alone, hidden from `md` up, takes the whole grid with it — AlignedSortFilterGroups hoists
  // a className every group shares onto the wrapper, and here that leaves the card with only its meta row.
  const selectOnly = props.panelOwnsSelect && !props.source
  return (
    <div data-slot="profile-card-skeleton" className="bg-card flex h-full flex-col gap-4 rounded-xl border p-5">
      <div className="flex items-start gap-3 @sm:gap-4">
        <Skeleton className="size-11 shrink-0 rounded-lg @sm:size-12" />
        <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            {/* The badge pill's own box: a `text-[10px]` line inside `py-0.5`, which no utility height lands on. */}
            <div className="mb-1 flex h-[19px] items-center">
              <Skeleton className="h-full w-28 rounded-full" />
            </div>
            <div className="flex min-h-[1lh] min-w-0 items-center gap-2 text-base leading-tight @sm:text-lg">
              {/* Which series this card is, exactly where the real card puts its chart dot. */}
              {props.compare && <Skeleton className="size-3 shrink-0 rounded-full" />}
              <Skeleton className="h-4 w-44" />
            </div>
            {/* The Puesto line the card holds open whether or not the person has one (profile-card.tsx). */}
            <div data-slot="profile-card-subtitle" className="flex min-h-[1lh] items-center text-xs @sm:text-sm">
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Skeleton className="size-9 rounded-md" />
            <Skeleton className="size-9 rounded-md" />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex min-h-[1lh] items-center gap-2 text-xs @sm:gap-3 @sm:text-sm">
          <Skeleton className="h-3 w-20 @sm:h-3.5" />
          <Skeleton className="h-3 w-32 @sm:h-3.5" />
          <Skeleton className="h-3 w-20 @sm:h-3.5" />
        </div>
        {/* AlignedSortFilterGroups' two tracks — the right-aligned label and its row of controls, per group. */}
        <div className={cn('grid grid-cols-[auto_minmax(0,1fr)] items-center gap-2', selectOnly && 'md:hidden')}>
          <Skeleton className={cn('h-2.5 w-10', props.panelOwnsSelect && !selectOnly && 'md:hidden')} />
          <Skeleton className={cn('h-7 w-48 rounded-md', props.panelOwnsSelect && !selectOnly && 'md:hidden')} />
          {props.source && (
            <>
              <Skeleton className="h-2.5 w-12" />
              <Skeleton className="h-7 w-44 rounded-md" />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * ReportControls' profile switch (comparison-profiles.tsx) — the two slots around the match score, which the
 * report grows the moment a SECOND profile is in the url, whichever of the three is selected.
 */
function ProfileSelectBarSkeleton() {
  return (
    <div
      data-slot="profile-switch-skeleton"
      className="flex min-w-0 items-center justify-center gap-2 py-2 @sm:gap-3 @sm:py-3"
    >
      <div className="bg-card inline-grid w-full max-w-full grid-cols-[1fr_auto_1fr] items-center gap-0.5 rounded-lg border p-0.5 @sm:w-auto @sm:gap-1 @sm:p-1">
        <ProfileSlotSkeleton />
        <Skeleton className="h-9 w-58 rounded-md" />
        <ProfileSlotSkeleton />
      </div>
    </div>
  )
}

/** One slot of that switch: the series dot, and the name only from `@xl`, where the real slot stops hiding it. */
function ProfileSlotSkeleton() {
  return (
    <div className="flex items-center justify-center gap-1 p-2 @sm:gap-1.5 @sm:px-2.5 @sm:py-2 @xl:w-36 @2xl:w-42">
      <Skeleton className="m-0.75 size-3 shrink-0 rounded-full" />
      <Skeleton className="hidden h-4 min-w-0 flex-1 @xl:block" />
    </div>
  )
}

// CollapsibleSection's header box, which carries no container query of its own (collapsible-section.tsx):
// a py-8 row of the size-10 icon, the text-xl title and its text-sm subtitle, closed by the collapse toggle.
function ReportSectionHeadingSkeleton() {
  return (
    <div className="flex w-full items-center gap-4 py-8">
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex items-center gap-3">
          <Skeleton className="size-10 shrink-0 rounded-xl" />
          <Skeleton className="h-5 w-44" />
        </div>
        <div className="flex h-5 items-center">
          <Skeleton className="h-3.5 w-64" />
        </div>
      </div>
      <Skeleton className="size-8 shrink-0 rounded-full" />
    </div>
  )
}

export function ReportBodySkeleton() {
  const params = useParams({ strict: false })
  // `deriveProfiles` rather than a second reading of the url: it is the one that answers "is a second profile
  // selected" for the report itself, and `applicantId` fills that slot from a param with nothing in the search.
  const twoProfiles = deriveProfiles(useSearch({ strict: false }), params, false).second != null
  // The card's control groups are the only part of it that varies with the url: the Select row belongs to the
  // section's own list panel from `md` up (panel-owns-select-by.tsx — Ofertas' applicant case aside, which needs
  // the viewer), and a team profile adds the Source row (profile-card.tsx).
  const card = {
    panelOwnsSelect:
      params.jobId != null ||
      params.offerId != null ||
      params.teamId != null ||
      params.groupId != null ||
      params.employeeId != null,
    source: params.teamId != null,
  }

  return (
    <div className="relative">
      {/* FetchingProgress' own box (report.tsx): a 1px bar under a `-mb-1`, so the column it heads starts 3px
          higher than the pane. Without it here the whole report jumped up by those 3px as the data landed. */}
      <div className="-mb-1 border-t border-transparent" />
      <div
        data-testid="report-body-skeleton"
        className="@container mx-auto flex w-full max-w-7xl flex-col px-3 @sm:px-4 @md:px-6 @lg:px-8"
      >
        <ProfileHeaderSkeleton />
        {/* Two cards and the switch beneath them, or one card and no switch — the same fork ComparisonProfiles
          takes, and it turns on the SECOND slot being filled, not on which of the three is selected. */}
        {twoProfiles ? (
          <>
            <div className="flex min-w-0 flex-col items-stretch gap-3 @2xl:flex-row">
              <div className="min-w-0 flex-1">
                <ProfileCardSkeleton compare {...card} />
              </div>
              <div className="min-w-0 flex-1">
                <ProfileCardSkeleton compare {...card} />
              </div>
            </div>
            <ProfileSelectBarSkeleton />
          </>
        ) : (
          <ProfileCardSkeleton {...card} />
        )}
        {/* Radar section — SECOND in the real report (report.tsx), behind the SAME container query: it is
          there from `@2xl` up and absent below, where the histogram is what follows the profile card. The
          skeleton carries the query too, so a wide report does not fill a histogram in where the radar
          lands and shove everything below it down. */}
        <div className="hidden @2xl:block print:block">
          <ReportSectionHeadingSkeleton />
          <div className="bg-card rounded-2xl border p-6 pt-2">
            {/* The chart's OWN box: `424/340` is the svg's intrinsic ratio and 848px its `max-w` (radar.tsx), and
              the disc is the 310-unit outer ring at that scale — so the card is already the 714px the radar
              makes it and nothing below has to move once it paints. */}
            <div className="mx-auto flex aspect-424/340 w-full max-w-[848px] items-center justify-center">
              <Skeleton className="aspect-square h-[78%] rounded-full" />
            </div>
          </div>
        </div>
        {/* Results section — the histogram that follows it: SkillsBreakdown's 3-column grid of bar cards
          (skills-breakdown.tsx). The columns collapse 3→2→1 with the same container queries as the real
          grid, so the fill-in never reflows. */}
        <ReportSectionHeadingSkeleton />
        <div className="grid gap-4 @xl:grid-cols-2 @2xl:grid-cols-3">
          {[0, 1, 2].map((col) => (
            <div key={col} className="bg-card flex flex-col gap-4 rounded-2xl border p-5">
              <div className="border-border/50 flex items-center justify-between border-b pb-3">
                <Skeleton className="h-3.5 w-28" />
              </div>
              <div className="flex flex-col gap-3">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex flex-col gap-3">
                    <div className="flex h-6 items-center justify-between gap-2">
                      <Skeleton className="h-3.5 w-24" />
                      <Skeleton className="h-3.5 w-4" />
                    </div>
                    <Skeleton className="h-3 w-full rounded-full" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function ReportPaneSkeleton() {
  return (
    <>
      <SectionHeaderSkeleton />
      <div className={cn(SCROLLABLE_AREA_CLASS, '@container')}>
        <ReportBodySkeleton />
      </div>
    </>
  )
}

/**
 * One member row (`member-item.tsx` and its three twins): an `Item` at `size="xs"` with `p-2`, whose 1px
 * transparent border is part of its height. The scores are a COLUMN of the row, not a line under the name,
 * and the actions keep their track whether or not a trigger is painted.
 */
function MemberRowSkeleton(props: { titleWidth: string; subWidth: string; subtitle?: boolean }) {
  return (
    <div
      data-slot="member-row-skeleton"
      className="flex w-full items-center gap-2 rounded-lg border border-transparent p-2"
    >
      <Skeleton className="size-8 shrink-0 rounded-md" />
      <div className="flex min-w-0 flex-1 flex-col">
        {/* The row's two type boxes, not two spacing steps: the real title and Puesto are what set its height. */}
        <div className="text-sm leading-snug">
          <Skeleton className={`inline-block h-3.5 align-middle ${props.titleWidth}`} />
        </div>
        {props.subtitle && (
          <div className="text-xs">
            <Skeleton className={`inline-block h-3 align-middle ${props.subWidth}`} />
          </div>
        )}
      </div>
      <span className={cn(SCORE_CLUSTER_CLASS, 'shrink-0')}>
        {SCORE_TRACKS.map((track) => (
          <span key={track} className={cn(SCORE_COLUMN_CLASS, track === 'global' && SCORE_GLOBAL_CLASS)}>
            <Skeleton className={cn(SCORE_BADGE_BOX, 'w-7')} />
          </span>
        ))}
      </span>
      <div className="w-6 shrink-0" />
    </div>
  )
}

const SCORE_TRACKS = ['skills', 'values', 'traits', 'global'] as const

// Pre-tag with a stable id so the render map keys by the element (not the array index): the widths
// repeat, so they can't be the key themselves.
const MEMBER_ROW_SKELETONS = [
  { title: 'w-2/5', sub: 'w-1/4' },
  { title: 'w-1/3', sub: 'w-1/5' },
  { title: 'w-1/2', sub: 'w-1/4' },
  { title: 'w-2/5', sub: 'w-1/3' },
  { title: 'w-1/3', sub: 'w-1/5' },
  { title: 'w-1/2', sub: 'w-1/4' },
  { title: 'w-2/5', sub: 'w-1/3' },
  { title: 'w-1/3', sub: 'w-1/5' },
].map((w, i) => ({ id: i, ...w }))

/** `subtitle`: the section whose member rows carry a Puesto under the name (see ListPanePending). */
export function MembersPanelSkeleton(props: { subtitle?: boolean }) {
  return (
    // `rule-fade-y` is the pane divider CollapsiblePanelContent carries (collapsible-panel.tsx) — the app's own
    // rule turned on its side, not a `border-r`, since a border takes no mask. Without it the skeleton showed
    // no line between this pane and the report, and one appeared as the panel arrived. `relative` because the
    // rule is a pseudo-element and this file's rules never declare a position of their own.
    <div className="rule-fade-y relative flex h-full w-full flex-col">
      <div className="border-b p-3">
        <Skeleton className="h-8 w-full rounded-lg" />
      </div>

      {/* Same single box as ScrollableList (`fitContent`). See ListPanelBodySkeleton. The strip heads it and
          carries the count, so the rows start straight under the hint. */}
      <div className="flex min-h-0 flex-col gap-1 overflow-hidden px-3 pb-3 max-md:shrink-0 max-md:grow md:flex-1">
        <StripSkeleton />
        <SelectionHintSkeleton />
        {MEMBER_ROW_SKELETONS.map((row) => (
          <MemberRowSkeleton key={row.id} titleWidth={row.title} subWidth={row.sub} subtitle={props.subtitle} />
        ))}
      </div>
    </div>
  )
}

// Mirrors the real ReportTabs markup (report-tabs.tsx → animated-tabs.tsx) so the placeholder lands
// exactly where the tabs render: same nav box (mx-4.5/gap-4/py-3.5), same per-tab Link box
// (h-12/gap-2/px-4) with a size-4 icon + label. Widths approximate the guaranteed Report/Distribution/
// Board labels; the label is `hidden sm:block` to match the real `hidden sm:inline` text (icon-only on mobile).
const REPORT_TAB_LABEL_WIDTHS = ['w-14', 'w-20', 'w-12']

export function ReportTabsSkeleton() {
  return (
    <div className="flex flex-col print:hidden">
      {/* Mirrors TransitionTabs: the faded rule and the row height both live on the WRAPPER, so the strip does
          not change height or swap a hard border for a fading one when the real tabs arrive. */}
      <div className="rule-fade-right relative shrink-0 md:h-[var(--chrome-tabs)]">
        <div className="flex gap-1">
          <nav className="mx-4.5 flex shrink-0 items-center justify-center gap-4 py-3.5">
            {REPORT_TAB_LABEL_WIDTHS.map((w) => (
              <div key={w} className="flex h-7 items-center">
                <div className="flex h-12 items-center gap-2 px-4">
                  <Skeleton className="size-4 shrink-0 rounded" />
                  <Skeleton className={`hidden h-4 rounded sm:block ${w}`} />
                </div>
              </div>
            ))}
          </nav>
        </div>
      </div>
    </div>
  )
}

export function ReportPanelSkeleton() {
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <ReportTabsSkeleton />
      <div className={SCROLLABLE_AREA_CLASS}>
        <ReportBodySkeleton />
      </div>
    </div>
  )
}

export { SettingsSectionSkeleton } from './settings/settings-section'
